<script setup lang="ts">
import { t } from '@/locales/i18n'
import { nextTick, ref, watch } from 'vue'
import { MAX_INPUT_CHARS } from '@/helpers/constantsNew'
import GwMenuNew from '@/components/lib/GwMenuNew/GwMenuNew.vue'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import GwDialogTemplate from '@/components/lib/GwDialogTemplate/GwDialogTemplate.vue'
import { type GwFloatIgnoredAttrs } from '@/components/lib/GwFloat/GwFloat.vue'

export interface GwTableInputMenuProps {
  text?: string
  tstPrefix?: string
  modalTitle?: string
  inputLabel?: string
  isPortalNested?: boolean
  contentAttrs?: Record<GwFloatIgnoredAttrs, string>
  customErrorHandler?: (value: string) => string
}

const props = withDefaults(defineProps<GwTableInputMenuProps>(), {
  text: '',
  tstPrefix: 'input_menu',
})

const l_text = ref(props.text)
const input = ref<HTMLInputElement | null>(null)
const customError = ref('')

const emit = defineEmits<{
  confirm: [text: string]
}>()

const handleOpen = () => {
  if (input.value) {
    input.value.focus()
  }
}
const handleClose = () => {
  nextTick(() => {
    l_text.value = props.text
    customError.value = ''
  })
}

const handleInput = (e: Event) => {
  l_text.value = (e.target as HTMLInputElement).value
  customError.value = ''
}

const handleConfirm = (closeMethod: () => void) => {
  const trimmedValue = l_text.value.trim()

  customError.value = props.customErrorHandler?.(trimmedValue) || ''

  if (!customError.value && trimmedValue && trimmedValue.length <= MAX_INPUT_CHARS) {
    emit('confirm', trimmedValue)
    closeMethod()
  }
}

watch(
  () => props.text,
  newVal => {
    l_text.value = newVal
  },
)
</script>
<template>
  <GwMenuNew
    transitionVariant="dialog"
    boxVariant="box_dialog"
    :isPortalNested
    :contentAttrs
    hasClose
    @open="handleOpen"
    @close="handleClose"
  >
    <template #opener="{ listeners }">
      <slot
        name="opener"
        :listeners
      ></slot>
    </template>
    <template #content="{ methods }">
      <GwDialogTemplate :modalTitle>
        <template #content>
          <div
            class="gw_input_wrapper"
            style="
              padding: var(--app-pad-4);
              --input-height: 2rem;
              --input-border-width: 1px;
              min-width: clamp(1px, 32rem, 100vw - 2rem);
            "
            :class="{
              gw_input_error: customError || l_text.length > MAX_INPUT_CHARS || !l_text.length,
            }"
          >
            <div
              v-if="inputLabel"
              class="text-medium clr-grey"
              style="margin-bottom: 0.25rem"
            >
              {{ inputLabel }}
            </div>
            <input
              ref="input"
              :data-tst="`${tstPrefix}_input`"
              autofocus
              contenteditable
              :value="l_text"
              class="textarea_note"
              @input="handleInput"
              @keydown.stop.enter.exact.prevent="() => handleConfirm(methods.close)"
            />
            <div
              :data-tst="`${tstPrefix}_error`"
              class="gw_input_error_message"
            >
              {{
                customError ||
                (l_text.length
                  ? t(`messages.field_name_too_many_characters`, {
                      max_chars: MAX_INPUT_CHARS,
                    })
                  : t(`messages.field_name_empty_error`, {
                      field_name: inputLabel || 'Name',
                    }))
              }}
            </div>
          </div>
        </template>

        <template #actions>
          <div
            class="d-flex w-100"
            style="gap: var(--app-gap-1)"
          >
            <GwBtn
              :data-tst="`${tstPrefix}_close`"
              variant="btn_text_grey_white"
              class="ml-auto"
              @click="methods.close()"
            >
              {{ t('btn.close') }}
            </GwBtn>
            <GwBtn
              :data-tst="`${tstPrefix}_confirm`"
              variant="btn_primary-v2"
              :disabled="!l_text.length || l_text.length > MAX_INPUT_CHARS"
              @click="() => handleConfirm(methods.close)"
            >
              {{ t('btn.confirm') }}
            </GwBtn>
          </div>
        </template>
      </GwDialogTemplate>
    </template>
  </GwMenuNew>
</template>

<style lang="scss" scoped>
.savedfiltersmenu {
  --btn-height: 1.75rem;
}
</style>
